import type { Locale } from 'date-fns';
import { enUS, uk } from 'date-fns/locale';
import enTranslations from '../locales/en.json';

/**
 * Unified Language Registry
 * Phase 2 freeze: only EN + UK ship while we stabilize. Other locales
 * decay faster than features. Add an entry + JSON file to re-enable one.
 */
export const LANGUAGE_REGISTRY = {
  en:       { name: 'English',           flag: '🇬🇧', dateFnsLocale: enUS, aliases: ['en-us', 'en-gb', 'en-au'], loader: () => import('../locales/en.json') },
  uk:       { name: 'Українська',        flag: '🇺🇦', dateFnsLocale: uk,   aliases: ['ua', 'uk-ua'], loader: () => import('../locales/uk.json') },
} as const;

export type SupportedLanguage = keyof typeof LANGUAGE_REGISTRY;

// Backward-compat: SUPPORTED_LANGUAGES shape { [code]: { name, flag } }
export const SUPPORTED_LANGUAGES: Record<SupportedLanguage, { name: string; flag: string }> = Object.fromEntries(
  Object.entries(LANGUAGE_REGISTRY).map(([code, { name, flag }]) => [code, { name, flag }])
) as any;

/**
 * Languages visible in user-facing pickers. Same as SUPPORTED_LANGUAGES
 * now that Phase 2 has frozen the registry itself to EN + UK.
 */
export const VISIBLE_LANGUAGES = SUPPORTED_LANGUAGES;

// Auto-generated date-fns locale map
export const DATE_LOCALE_MAP: Record<SupportedLanguage, Locale> = Object.fromEntries(
  Object.entries(LANGUAGE_REGISTRY).map(([code, { dateFnsLocale }]) => [code, dateFnsLocale])
) as any;

// ── Normalizer (auto-generated from registry aliases) ──────────────────
const _aliasMap = new Map<string, SupportedLanguage>();
const _basePrefixMap = new Map<string, SupportedLanguage>();

for (const [code, { aliases }] of Object.entries(LANGUAGE_REGISTRY)) {
  const lang = code as SupportedLanguage;
  // exact code
  _aliasMap.set(code.toLowerCase(), lang);
  // explicit aliases
  for (const alias of aliases) {
    _aliasMap.set(alias.toLowerCase(), lang);
  }
  // base prefix (first 2 chars) — first registration wins (en before es-419)
  const base = code.split('-')[0].toLowerCase();
  if (!_basePrefixMap.has(base)) {
    _basePrefixMap.set(base, lang);
  }
}

export const normalizeLanguage = (rawLang?: string): SupportedLanguage => {
  if (!rawLang) return 'en';
  const normalized = rawLang.toLowerCase().trim();

  // 1. Exact match (code or alias)
  if (_aliasMap.has(normalized)) return _aliasMap.get(normalized)!;

  // 2. Try with hyphen variants  (e.g. "pt-br" → pt-BR)
  const withHyphen = normalized.replace('_', '-');
  if (_aliasMap.has(withHyphen)) return _aliasMap.get(withHyphen)!;

  // 3. Prefix match (first 2 chars)
  const base = normalized.split('-')[0].split('_')[0];
  if (_basePrefixMap.has(base)) return _basePrefixMap.get(base)!;

  return 'en';
};

// Device language detection
export const detectDeviceLanguage = (): SupportedLanguage => {
  const deviceLang = navigator.language || (navigator.languages && navigator.languages[0]) || 'en';
  return normalizeLanguage(deviceLang);
};

// ── Lazy translation loading ───────────────────────────────────────────
const _translationCache = new Map<SupportedLanguage, Record<string, any>>();
// English is always eagerly loaded as fallback
_translationCache.set('en', enTranslations);

export const loadTranslations = async (lang: SupportedLanguage): Promise<Record<string, any>> => {
  if (_translationCache.has(lang)) return _translationCache.get(lang)!;
  try {
    const module = await LANGUAGE_REGISTRY[lang].loader();
    const translations = (module as any).default || module;
    _translationCache.set(lang, translations);
    return translations;
  } catch (e) {
    console.warn(`Failed to load translations for ${lang}, falling back to en`, e);
    return _translationCache.get('en')!;
  }
};

export const getTranslationsSync = (lang: SupportedLanguage): Record<string, any> => {
  return _translationCache.get(lang) || _translationCache.get('en')!;
};

// ── Nested value getter ────────────────────────────────────────────────
const getNestedValue = (obj: Record<string, any>, path: string): string | undefined => {
  const keys = path.split('.');
  let current: any = obj;
  for (const key of keys) {
    if (current === undefined || current === null || typeof current !== 'object') return undefined;
    current = current[key];
  }
  return typeof current === 'string' ? current : undefined;
};

// ── Translator ─────────────────────────────────────────────────────────
export const createTranslator = (lang: SupportedLanguage) => {
  const normalizedLang = normalizeLanguage(lang);
  const translations = getTranslationsSync(normalizedLang);
  const enFallback = _translationCache.get('en')!;

  return (key: string, variables?: Record<string, any>): string => {
    let text =
      getNestedValue(translations, key) ||
      translations[key] ||
      getNestedValue(enFallback, key) ||
      enFallback[key] ||
      key;

    if (variables) {
      Object.entries(variables).forEach(([varKey, value]) => {
        text = text.replace(new RegExp(`\\{${varKey}\\}`, 'g'), String(value));
      });
    }
    return text;
  };
};

// ── Helpers ────────────────────────────────────────────────────────────
export const getTranslationsCount = (lang: SupportedLanguage): number => {
  return Object.keys(getTranslationsSync(lang) || {}).length;
};

/** Returns all keys (flattened dot-notation) from a translations object */
export const flattenKeys = (obj: Record<string, any>, prefix = ''): string[] => {
  const keys: string[] = [];
  for (const [k, v] of Object.entries(obj)) {
    const fullKey = prefix ? `${prefix}.${k}` : k;
    if (typeof v === 'object' && v !== null && !Array.isArray(v)) {
      keys.push(...flattenKeys(v, fullKey));
    } else {
      keys.push(fullKey);
    }
  }
  return keys;
};

/** Compare a locale's keys against English and return missing/extra */
export const diffLocaleKeys = (lang: SupportedLanguage): { missing: string[]; extra: string[] } => {
  const enKeys = new Set(flattenKeys(_translationCache.get('en')!));
  const langTranslations = getTranslationsSync(lang);
  const langKeys = new Set(flattenKeys(langTranslations));

  const missing = [...enKeys].filter(k => !langKeys.has(k));
  const extra = [...langKeys].filter(k => !enKeys.has(k));
  return { missing, extra };
};

// ── Translation Length Auditing ────────────────────────────────────────
interface LengthViolation {
  key: string;
  value: string;
  length: number;
  limit: number;
  context: 'button' | 'header' | 'short';
}

const BUTTON_PATTERNS = [/\.add/, /\.button/, /\.cta/, /\.save/, /\.confirm/, /\.submit/, /\.cancel/, /\.delete/, /\.remove/];
const HEADER_PATTERNS = [/\.title$/, /\.heading/, /^nav\./, /\.label$/];
const SHORT_PATTERNS = [/\.short\./, /\.abbr/, /\.brief/];

const LENGTH_LIMITS = {
  button: 30,
  header: 50,
  short: 15,
};

const detectContext = (key: string): 'button' | 'header' | 'short' | null => {
  if (SHORT_PATTERNS.some(p => p.test(key))) return 'short';
  if (BUTTON_PATTERNS.some(p => p.test(key))) return 'button';
  if (HEADER_PATTERNS.some(p => p.test(key))) return 'header';
  return null;
};

/** Audit translation values for length issues in context-sensitive keys */
export const auditTranslationLengths = (lang: SupportedLanguage): LengthViolation[] => {
  const translations = getTranslationsSync(lang);
  const keys = flattenKeys(translations);
  const violations: LengthViolation[] = [];

  const getNestedValue = (obj: Record<string, any>, path: string): string | undefined => {
    const parts = path.split('.');
    let current: any = obj;
    for (const part of parts) {
      if (current === undefined || current === null) return undefined;
      current = current[part];
    }
    return typeof current === 'string' ? current : undefined;
  };

  for (const key of keys) {
    const context = detectContext(key);
    if (!context) continue;
    
    const value = getNestedValue(translations, key);
    if (!value) continue;
    
    const limit = LENGTH_LIMITS[context];
    if (value.length > limit) {
      violations.push({ key, value, length: value.length, limit, context });
    }
  }

  return violations;
};
